import React, { useState, useEffect } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import InputAdornment from "@material-ui/core/InputAdornment";
import Typography from "@material-ui/core/Typography";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
}));

const ServiceTypeSlaSchema = Yup.object().shape({
  queueId: Yup.string().required("Escolha uma fila"),
  tempoLimiteMinutos: Yup.number()
    .typeError("Informe um número")
    .min(1, "Tem que ser pelo menos 1 minuto")
    .required("Obrigatório"),
  descricao: Yup.string().max(255, "Descrição muito longa"),
});

const ServiceTypeSlaModal = ({ open, onClose, slaId, queues, onSaved }) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);

  const initialState = {
    queueId: "",
    tempoLimiteMinutos: 30,
    descricao: "",
    ativo: true,
  };

  const [sla, setSla] = useState(initialState);

  useEffect(() => {
    const fetchSla = async () => {
      if (!slaId) {
        setSla(initialState);
        return;
      }
      try {
        const { data } = await api.get(`/service-type-sla/${slaId}`);
        setSla({
          queueId: data.queueId || "",
          tempoLimiteMinutos: data.tempoLimiteMinutos || 30,
          descricao: data.descricao || "",
          ativo: data.ativo != null ? data.ativo : true,
        });
      } catch (err) {
        toastError(err);
      }
    };
    if (open) fetchSla();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slaId, open]);

  const handleClose = () => {
    setSla(initialState);
    onClose();
  };

  const handleSaveSla = async (values) => {
    const payload = {
      ...values,
      tempoLimiteMinutos: parseInt(values.tempoLimiteMinutos, 10),
    };
    setLoading(true);
    try {
      if (slaId) {
        await api.put(`/service-type-sla/${slaId}`, payload);
      } else {
        await api.post("/service-type-sla", payload);
      }
      toast.success(slaId ? "Regra de SLA atualizada!" : "Regra de SLA criada!");
      if (onSaved) onSaved();
      handleClose();
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth scroll="paper">
      <DialogTitle>{slaId ? "Editar regra de SLA" : "Nova regra de SLA"}</DialogTitle>
      <Formik
        initialValues={sla}
        enableReinitialize
        validationSchema={ServiceTypeSlaSchema}
        onSubmit={(values, actions) => {
          setTimeout(() => {
            handleSaveSla(values);
            actions.setSubmitting(false);
          }, 300);
        }}
      >
        {({ values, touched, errors, isSubmitting, setFieldValue }) => (
          <Form>
            <DialogContent dividers className={classes.root}>
              <Typography variant="caption" color="textSecondary">
                Tempo máximo que um cliente pode ficar esperando nesta fila antes de gerar um alerta pros supervisores.
              </Typography>

              <Field
                as={TextField}
                select
                fullWidth
                variant="outlined"
                margin="dense"
                label="Fila"
                name="queueId"
                SelectProps={{ native: true }}
                error={touched.queueId && Boolean(errors.queueId)}
                helperText={touched.queueId && errors.queueId}
              >
                <option value="" disabled></option>
                {queues.map((q) => (
                  <option key={q.id} value={q.id}>
                    {q.name}
                  </option>
                ))}
              </Field>

              <Field
                as={TextField}
                fullWidth
                variant="outlined"
                margin="dense"
                type="number"
                label="Tempo limite"
                name="tempoLimiteMinutos"
                InputProps={{
                  endAdornment: <InputAdornment position="end">minutos</InputAdornment>,
                }}
                error={touched.tempoLimiteMinutos && Boolean(errors.tempoLimiteMinutos)}
                helperText={touched.tempoLimiteMinutos && errors.tempoLimiteMinutos}
              />

              <Field
                as={TextField}
                fullWidth
                variant="outlined"
                margin="dense"
                label="Descrição (opcional)"
                name="descricao"
                placeholder="Ex: SLA prioritário para clientes VIP"
                error={touched.descricao && Boolean(errors.descricao)}
                helperText={touched.descricao && errors.descricao}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={values.ativo}
                    onChange={(e) => setFieldValue("ativo", e.target.checked)}
                    color="primary"
                  />
                }
                label={values.ativo ? "Ativa" : "Inativa"}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose} color="secondary" disabled={isSubmitting}>
                Cancelar
              </Button>
              <Button
                type="submit"
                color="primary"
                variant="contained"
                disabled={isSubmitting}
                className={classes.btnWrapper}
              >
                {slaId ? "Salvar" : "Criar"}
                {isSubmitting && <CircularProgress size={24} className={classes.buttonProgress} />}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default ServiceTypeSlaModal;
